import React, { useState } from 'react';
import * as R from 'ramda';
import styled from 'styled-components';
import { OverlayTrigger } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Paginations from './Pagination';
import SnippetDetails from './SnippetDetails';
import ReactTooltip from 'react-tooltip';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { storage } from '../../services/firebase';
import {
  DataTable,
  DataTableCell,
  DataTableHeadCell,
  DataTableHeadRow,
  DataTableRow,
  DataTableShell,
} from '../../components/ui';

const DownloadCell = styled(DataTableCell)`
  text-align: center;

  a {
    color: #5eead4;
    font-weight: 500;
  }

  a:hover {
    color: #34d399;
  }
`;

const SnippetCell = styled(DataTableCell)`
  word-break: break-all;
  width: 40%;

  em {
    font-style: normal;
    font-weight: 700;
    color: #f8fafc;
  }

  & > div {
    font-size: 13px;
    min-height: 100px;
  }

  & span {
    font-weight: 500;
    color: #5eead4;
    cursor: pointer;
    display: flex;
    justify-content: flex-end;
  }
`;

const doStorageDownload = R.contains(R.__, ['tceq_novell']);
const getStorageRef = R.cond<string, ReturnType<typeof getStorage> | null>([
  [
    R.equals('tceq_novell'),
    R.always(
      getStorage(undefined, `gs://${process.env.REACT_APP_STORAGE_BUCKET_NOVELL}`)
    ),
  ],
  [R.T, R.always(null)],
]);

interface IContent {
  environmenData: any;
  queryText: any;
  sentimentAnalyzer: (keyword: any, from: number) => void;
  setIsLoading: any;
  tone: 'positive' | 'negative';
}

const TableContent: React.FC<IContent> = (props) => {
  const [downloadTriggers, setDownloadTriggers] = useState<string[]>([]);

  return (
    <>
      <DataTableShell tone={props.tone}>
        <DataTable>
          <thead>
            <DataTableHeadRow tone={props.tone} sticky topOffset={64}>
              <DataTableHeadCell>Rank</DataTableHeadCell>
              <DataTableHeadCell style={{ width: '100px' }}>Sentiment Score</DataTableHeadCell>
              <DataTableHeadCell>Text Snippet</DataTableHeadCell>
              <DataTableHeadCell>Entity's File</DataTableHeadCell>
              <DataTableHeadCell>Document</DataTableHeadCell>
            </DataTableHeadRow>
          </thead>
          <tbody>
            {typeof props.environmenData.data !== 'undefined' &&
              props.environmenData.data.length > 0 &&
              props.environmenData.data.map((record: any, index: any) => (
                <DataTableRow key={record.rank} tone={props.tone} striped index={index}>
                  <DataTableCell>#{record.rank}</DataTableCell>
                  <DataTableCell>{record.sentiment_score}</DataTableCell>
                  <SnippetCell>
                    <SnippetDetails
                      details={
                        record.snippet
                          ? record.snippet.replace(`/[^\x00-\x7F]/g`, '')
                          : ''
                      }
                    />
                  </SnippetCell>
                  <DataTableCell>{record.entity_name}</DataTableCell>
                  <OverlayTrigger
                    placement="bottom"
                    key={index}
                    overlay={<ReactTooltip effect="solid" />}
                  >
                    <DownloadCell
                      onClick={() => {
                        if (doStorageDownload(record.doc_url)) {
                          const storageRef = getStorageRef(record.entitySource);
                          if (R.isNil(storageRef)) {
                            return toast.error(`Oops, could not find the file.`);
                          }
                          setDownloadTriggers(
                            R.append(index + 1, downloadTriggers)
                          );
                          return getDownloadURL(ref(storage, record.fileName))
                            .then((url) => window.open(url, '_blank', 'noopener,noreferrer'))
                            .catch((err) => toast.error(err.message))
                            .finally(() =>
                              setDownloadTriggers(R.without([index + 1], downloadTriggers))
                            );
                        }
                        if (!record.doc_url) {
                          return toast.warn(
                            <span>
                              No associated file was found for the content id{' '}
                              <b>{record.doc_source}</b>
                            </span>
                          );
                        }
                        window.open(record.doc_url, '_blank');
                      }}
                    >
                      <a href={record.doc_url} data-tip="Click to download">{record.doc_source}</a>
                    </DownloadCell>
                  </OverlayTrigger>
                </DataTableRow>
              ))}
          </tbody>
        </DataTable>
      </DataTableShell>

      <Paginations
        isDisabled={false}
        isFirstPage={
          (props.environmenData &&
            props.environmenData.records_num_from === 0) ||
          false
        }
        isLastPage={
          (props.environmenData &&
            props.environmenData.records_num_from +
              props.environmenData.records_num_show >=
              props.environmenData.total_records) ||
          false
        }
        totalRecords={
          (props.environmenData && props.environmenData.total_records) || 0
        }
        onPreviousClick={() => {
          if (!props.environmenData) {
            return;
          }
          const currentFrom = props.environmenData.records_num_from;
          const currentSize = props.environmenData.records_num_show;
          props.setIsLoading(true);
          return props.sentimentAnalyzer(
            props.environmenData.sorting,
            currentFrom - currentSize
          );
        }}
        onNextClick={() => {
          if (!props.environmenData) {
            return;
          }
          const currentFrom = props.environmenData.records_num_from;
          const currentSize = props.environmenData.records_num_show;
          return props.sentimentAnalyzer(
            props.environmenData.sorting,
            currentFrom + currentSize
          );
        }}
      />
    </>
  );
};
export default TableContent;
